import { useCallback, useEffect, useState } from 'react';

import { Alert } from '@zooplus/ui-components';

import { getRetryData } from '../../services/retryPaymentService';
import { PspConnector, RetryPaymentMethod } from '../../types';
import { useAppContext } from '../AppContextProvider';
import { useThunkDispatch } from '../providers/ThunkDispatchProvider';

import { LocalPaymentsRetryInitiator } from './LocalPaymentsRetryInitiator';
import RetryPaymentDefault from './payment/RetryPaymentDefault';
import RetryPaymentIdeal from './payment/RetryPaymentIdeal';
import RetryPaymentNotification from './payment/RetryPaymentNotification';
import RetryPaymentPaypal from './payment/RetryPaymentPaypal';
import { useRetryState } from './RetryDataProvider';
import RetryPaymentContextProvider, { useRetryPaymentContext } from './RetryPaymentContextProvider';
import { isReturnUrlFromAdyen } from './utils';

export type RetryPaymentSwitchProps = {
  orderId: string;
  customerId: string;
};

const RetryPaymentSwitch: React.FC<RetryPaymentSwitchProps> = ({ orderId, customerId }) => {
  const {
    retryState: { retryData: retryPaymentData },
  } = useRetryState();

  const { shopBundle } = useAppContext();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { thunkDispatch } = useThunkDispatch();

  useEffect(() => {
    const url = window.location.href;

    const isAuthorizationResult = isReturnUrlFromAdyen(url);
    thunkDispatch(
      getRetryData({ orderId, customerId, shopBundle, isAuthorizationResult, pspResponseUrl: url }),
    );
  }, [orderId, customerId, shopBundle]);

  const handleError = useCallback((errorMessage: string) => {
    setErrorMessage(errorMessage);
  }, []);

  if (errorMessage) {
    return (
      <Alert variant="warning" data-zta="errorPaymentRetry">
        {errorMessage}
      </Alert>
    );
  }

  if (!retryPaymentData) {
    return null;
  }

  const {
    idealAvailable,
    retryPossible,
    retryPaymentMethod,
    retryPaymentURL,
    retryPaymentStatus,
    currency,
    amount,
    siteId,
    connector,
    pspToken,
  } = retryPaymentData;

  return (
    <RetryPaymentContextProvider
      retryPossible={retryPossible}
      retryPaymentStatus={retryPaymentStatus}>
      <RetryPaymentSwitchWrapped
        currency={currency}
        orderId={orderId}
        customerId={customerId}
        idealAvailable={idealAvailable}
        retryPaymentMethod={retryPaymentMethod}
        retryPaymentUrl={retryPaymentURL}
        amount={amount}
        siteId={siteId}
        connector={connector}
        pspToken={pspToken}
        onError={handleError}
      />
    </RetryPaymentContextProvider>
  );
};

type RetryPaymentSwitchWrappedProps = {
  currency: string;
  orderId: string;
  customerId: string;
  idealAvailable: boolean;
  retryPaymentMethod: RetryPaymentMethod;
  retryPaymentUrl?: string;
  amount: number;
  siteId: number;
  connector: PspConnector;
  pspToken?: string;
  onError: (errorMessage: string) => void;
};

const RetryPaymentSwitchWrapped: React.FC<RetryPaymentSwitchWrappedProps> = ({
  currency,
  orderId,
  customerId,
  idealAvailable,
  retryPaymentMethod,
  retryPaymentUrl,
  amount,
  siteId,
  connector,
  pspToken,
  onError,
}) => {
  const {
    retryPaymentInfo: { retryPossible, retryPaymentStatus },
  } = useRetryPaymentContext();

  const isUseNewFlow = [
    'DOTPAY',
    'ONLINE_BANKING_PL',
    'PAYTRAIL',
    'BANCONTACT_MOBILE',
    'TWINT',
  ].some((paymentMethod) => paymentMethod === retryPaymentMethod);

  const isIdealRetry = idealAvailable && retryPossible;
  const isPaypalRetry = retryPaymentMethod === 'PAYPAL' && retryPossible;
  const isDefaultRetry =
    !isUseNewFlow && !idealAvailable && retryPaymentMethod !== 'PAYPAL' && retryPossible;

  return (
    <>
      {isUseNewFlow && (
        <LocalPaymentsRetryInitiator
          orderId={orderId}
          customerId={customerId}
          isRetryPossible={retryPossible}
          retryPaymentMethod={retryPaymentMethod}
        />
      )}
      {isIdealRetry && <RetryPaymentIdeal onError={onError} orderId={orderId} />}
      {isPaypalRetry && (
        <RetryPaymentPaypal currency={currency} amount={amount} orderId={orderId} siteId={siteId} />
      )}
      {isDefaultRetry && (
        <RetryPaymentDefault
          retryPaymentUrl={retryPaymentUrl}
          retryPaymentMethod={retryPaymentMethod}
          retryPaymentStatus={retryPaymentStatus}
          orderId={orderId}
          connector={connector}
          pspToken={pspToken}
          amount={amount}
          currency={currency}
          siteId={siteId}
        />
      )}
      {!isUseNewFlow && retryPaymentStatus && (
        <RetryPaymentNotification
          retryPaymentStatus={retryPaymentStatus}
          retryPaymentMethod={retryPaymentMethod}
          orderId={orderId}
        />
      )}
    </>
  );
};

export default RetryPaymentSwitch;
